import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/admin/api";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { COLORS } from "@/comiq/tokens";
import { Building2, FileText, Layers, MailPlus, ArrowRight } from "lucide-react";

const WAVE_STATUS = {
  draft: { label: "Draf", fg: "#57534E", bg: "#EEEAE0" },
  pilot: { label: "Pilot", fg: "#1D4ED8", bg: "#DBEAFE" },
  live: { label: "Live", fg: "#15803D", bg: "#DCFCE7" },
  closed: { label: "Ditutup", fg: "#B45309", bg: "#FEF3C7" },
  archived: { label: "Arsip", fg: "#57534E", bg: "#EEEAE0" },
};

export const WaveBadge = ({ status }) => {
  const s = WAVE_STATUS[status] || WAVE_STATUS.draft;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color: s.fg, background: s.bg }} data-testid={`wave-status-${status}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.fg }} />
      {s.label}
    </span>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [waves, setWaves] = useState([]);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/waves").then((r) => setWaves(r.data.slice(0, 5))).catch(() => {});
    api.get("/admin/audit?limit=8").then((r) => setAudit(r.data)).catch(() => setAudit([]));
  }, []);

  const cards = [
    { icon: Building2, label: "Organisasi", value: data?.organizations ?? "—", to: "/admin/organizations" },
    { icon: FileText, label: "Studi", value: data?.studies ?? "—", to: "/admin/waves" },
    { icon: Layers, label: "Gelombang live", value: data?.waves_by_status?.live ?? 0, to: "/admin/waves" },
    { icon: MailPlus, label: "Undangan terkirim*", value: data?.invitations_by_status?.invited ?? 0, to: "/admin/invitations" },
  ];

  return (
    <div className="p-6">
      <Eyebrow>Research Admin</Eyebrow>
      <h1 className="font-serif-editorial mt-1 text-3xl" style={{ color: COLORS.ink }}>Dashboard</h1>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} data-testid={`stat-${c.label}`} className="rounded-comiq border bg-comiq-surface-alt p-5 transition-transform hover:-translate-y-0.5" style={{ borderColor: COLORS.line }}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium" style={{ color: COLORS.textSoft }}>{c.label}</span>
              <c.icon size={16} style={{ color: COLORS.orangeInk }} />
            </div>
            <div className="mt-2 text-2xl font-bold" style={{ color: COLORS.ink }}>{c.value}</div>
          </Link>
        ))}
      </div>
      <p className="mt-2 text-xs" style={{ color: COLORS.textSoft }}>*Tautan dibuat &amp; diekspor via CSV — pengiriman email/OTP belum aktif pada fase ini.</p>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-comiq border bg-comiq-surface-alt" style={{ borderColor: COLORS.line }}>
          <div className="flex items-center justify-between border-b px-5 py-3.5" style={{ borderColor: COLORS.line }}>
            <h2 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Gelombang terbaru</h2>
            <Link to="/admin/waves" className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: COLORS.orangeInk }}>Semua <ArrowRight size={13} /></Link>
          </div>
          <div className="divide-y" style={{ borderColor: COLORS.line }}>
            {waves.length === 0 && <p className="px-5 py-6 text-sm" style={{ color: COLORS.textSoft }}>Belum ada gelombang.</p>}
            {waves.map((w) => (
              <div key={w.wave_id} className="flex items-center justify-between px-5 py-3" data-testid={`wave-row-${w.wave_id}`}>
                <div>
                  <div className="text-sm font-medium" style={{ color: COLORS.ink }}>{w.label}</div>
                  {w.is_demo && <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "#B45309" }}>Data demo</span>}
                </div>
                <WaveBadge status={w.status} />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-comiq border bg-comiq-surface-alt" style={{ borderColor: COLORS.line }}>
          <div className="border-b px-5 py-3.5" style={{ borderColor: COLORS.line }}>
            <h2 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Aktivitas admin terakhir</h2>
          </div>
          <div className="divide-y" style={{ borderColor: COLORS.line }}>
            {audit.length === 0 && <p className="px-5 py-6 text-sm" style={{ color: COLORS.textSoft }}>Belum ada aktivitas.</p>}
            {audit.map((e) => (
              <div key={e.event_id} className="px-5 py-3 text-sm">
                <span className="font-mono text-xs font-semibold" style={{ color: COLORS.ink }}>{e.action}</span>
                <span className="ml-2 text-xs" style={{ color: COLORS.textSoft }}>{e.actor_email}</span>
                <div className="text-[11px]" style={{ color: COLORS.textSoft }}>{new Date(e.created_at).toLocaleString("id-ID")}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
